import * as angular from "angular";
import * as _ from "lodash";
import {OmsOrdersGroupType} from "../../oms/types/omsOrdersGroup.type";
import {SiteWeightUnit} from "../types/siteWeightUnit.type";
import {ItinerariesTasksSettings} from "../../agents/angular/models/itineraries-tasks-settings.model";
import {IWindowService} from "angular";
import {PlanningRouteOverviewSettingsModel} from "../../planning/models/planning-route-overview-settings.model";
import {PackageFieldConfigurationModel} from "../../site-preferences/models/package-field-configuration.model";
import {PackageConfigurationEntity} from "../../site-preferences/models/package-configuration.model";
import {SiteContextService} from "./site-context.service";
import {ServicesViewsStateType} from "../../deliveries/types/services-views-state.type";
import {CustomFieldModel} from "../../site-preferences/models/custom-field.model";
import {FirebaseService} from "../../persistence-layer/firebase.service";
import {ServicesAssignResourceColumnsModel} from "../../deliveries/models/services-assign-resource-columns.model";
import {Direction} from "@angular/cdk/bidi";
import {ServiceStatus} from "../../deliveries/constants/service-status.type";
import {SuperSearchSortMethodType} from "../../search/types/super-search-sort-method.type";

// this is to bypass the older version of
// ecmascript in the project settings
// it should work in all modern browsers
interface IntlLocaleWithTextInfo extends Intl.Locale {
  getTextInfo(): any;
}

export class SplitService {

  storage: any;

  constructor(private $window: IWindowService, private siteContextService: SiteContextService, private $translate,
              private planningConstants,
              private splitConstants, private deliveriesConstants, private omsConstants,
              private iso4217, private moment, private agentConstants, private expensesConstants,
              private firebaseService: FirebaseService,
              private FulfillmentConstants, private billingOrdersConstants, private commonsConstants, private leanPlanningConstants,
              private timekeepingConstants) {
    this.storage = this.$window.localStorage;
  }

  getIsSite(): boolean {
    return this.siteContextService.isSite();
  }

  getSiteInfo() {
    return this.siteContextService.getSiteInfo();
  }

  getMemberPermissions() {
    return this.siteContextService.getMemberPermissions();
  }

  getOrgId() {
    return this.siteContextService.getSiteInfo().organizationId;
  }

  getSiteId(): string {
    return this.siteContextService.getSiteInfo().siteId;
  }

  getSiteName() {
    return this.siteContextService.getSiteInfo().siteName;
  }

  isCreatingRouteOnlyFromGeoServer() {
    return this.siteContextService.getSiteInfo().creatingRouteOnlyFromGeoServer;
  }

  getTimeZone() {
    // var siteInfo = siteContextService.getSiteInfo();
    // return siteInfo.timezone;
    const timezone = this.storage.getItem('TIME_ZONE_KEY');
    return timezone ? timezone : this.moment.tz.guess();
  }

  getTimeZoneOffsetByDate(timeZoneId, date) {
    if (timeZoneId) {
      return this.moment.tz.zone(timeZoneId).utcOffset(date);
    }
  }

  getTimeZoneOffsetDiff(taskTimeZoneId, notBefore) {
    const configTimeZoneId = this.getTimeZone();
    const configTimeZoneOffset = this.getTimeZoneOffsetByDate(configTimeZoneId, notBefore);
    const taskTimeZoneOffset = this.getTimeZoneOffsetByDate(taskTimeZoneId, notBefore);

    if (!_.isUndefined(taskTimeZoneOffset) && !_.isUndefined(configTimeZoneOffset)) {
      return (configTimeZoneOffset - taskTimeZoneOffset) / 60;
    } else {
      return 0;
    }
  }

  getStartDayTimestamp(date) {
    return this.moment(this.moment(date).local().toISOString()).startOf('day').valueOf();
  }

  getEndDayTimestamp(date) {
    return this.moment(this.moment(date).local().toISOString()).endOf('day').valueOf();
  }

  getNewDateFormat(date) {
    return new Date(this.moment(date).local().toISOString());
  }

  getStartDayNewDateFormat(date) {
    return new Date(this.moment(date).local().startOf('day').toISOString());
  }

  getTasksGeoFencingRadius() {
    const siteInfo = this.siteContextService.getSiteInfo();
    return {
      geofencingArrivalTaskRadius: siteInfo.geofencingArrivalTaskRadius,
      geofencingDepartureTaskRadius: siteInfo.geofencingDepartureTaskRadius
    };
  }

  getSitePackageItems() {
    return this.siteContextService.getSiteInfo().itemSettings || [];
  }

  getSitePackageItemsMap(keyBy?: string) {
    const packageItems = this.siteContextService.getSiteInfo().itemSettings || [];
    return _.keyBy(packageItems, (keyBy || 'id'))
  }

  getSiteLabels() {
    return this.siteContextService.getSiteInfo().labels || [];
  }

  getSiteResourceSkills() {
    return this.siteContextService.getSiteInfo().resourceSkills || [];
  }

  getSiteSkills() {
    return this.siteContextService.getSiteInfo().skills || [];
  }

  getUserSiteBranchIds() {
    return this.siteContextService.getSiteInfo().restrictedBranches || [];
  }

  getSiteProgramTypesMap() {
    return this.siteContextService.getSiteInfo().programs || {};
  }

  getSiteProgramTypes() {
    return _.map(this.siteContextService.getSiteInfo().programs || {}, (value, key) => {
      return {id: key, type: value};
    });
  }

  getSiteLogoUrl() {
    return this.siteContextService.getSiteInfo().logoUrl;
  }

  getSiteAddress() {
    return this.siteContextService.getSiteInfo().address;
  }

  getSitePasswordPolicy() {
    return this.siteContextService.getSiteInfo().passwordPolicy;
  }

  setSitePasswordPolicy(passwordPolicy) {
    const siteInfo = this.siteContextService.getSiteInfo();
    siteInfo.passwordPolicy = passwordPolicy;
  }

  getSiteDistanceSmallUnit(isShort?) {
    const unitRef = this.siteContextService.getSiteInfo().unit;
    return unitRef === 'IMPERIAL' ? (isShort ? 'MI' : 'Foot') : (isShort ? 'KM' : 'Meter');
  }

  getSiteDistanceLargeUnit(isShort?) {
    const unitRef = this.siteContextService.getSiteInfo().unit;
    return unitRef === 'IMPERIAL' ? (isShort ? 'MI' : 'Miles') : (isShort ? 'KM' : 'Kilometers');
  }

  getTemperatureUnit(isShort?: boolean) {
    const unitRef = this.siteContextService.getSiteInfo().unit;
    return unitRef === 'IMPERIAL' ? (isShort ? '°F' : 'Fahrenheit') : (isShort ? '°C' : 'Celsius');
  }

  isImperial(): boolean {
    return this.siteContextService.getSiteInfo().unit === 'IMPERIAL';
  }

  getSiteExternalIdConfig() {
    return !!this.siteContextService.getSiteInfo().displayExternalId;
  }

  getIsAgentExternalIdMandatory(): boolean {
    const siteInfo = this.siteContextService.getSiteInfo();
    return !!_.get(siteInfo, 'agentCustomizedFields.agentExternalIdIsMandatory');
  }

  getSiteAgentAssistantMode() {
    return !!this.siteContextService.getSiteInfo().agentAssistantEnabled;
  }

  getSiteUseGoogleAddressValidation() {
    return !!this.siteContextService.getSiteInfo().useGoogleAddressValidation;
  }

  getCurrencyCode() {
    return this.siteContextService.getSiteInfo().currency || 'USD'
  }

  getLocaleCode() {
    return (this.siteContextService.isSite() ? this.siteContextService.getSiteInfo().language : undefined) || 'en_US'
  }

  getCurrencySymbol() {
    return this.iso4217.getCurrencyByCode(this.getCurrencyCode()).symbol;
  }

  getCountryCode() {
    return this.siteContextService.getSiteInfo().country || 'US';
  }

  getSiteStates() {
    const countryCode = this.getCountryCode();
    if (countryCode === 'MX') {
      return this.splitConstants.mexicoStates;
    } else {
      return this.splitConstants.states;
    }
  }

  // function getRegionsByLanguage() {
  //   var languageSelected = $window.localStorage.getItem('NG_TRANSLATE_LANG_KEY');
  //   if (languageSelected == 'es') {
  //     return splitConstants.countriesInSpanish;
  //   } else {
  //     return splitConstants.countries;
  //   }
  // }

  getLayoutDirection(): Direction {
    const language: string = this.$window.localStorage.getItem('NG_TRANSLATE_LANG_KEY');
    const locale: IntlLocaleWithTextInfo = new Intl.Locale(language) as IntlLocaleWithTextInfo;
    return locale.getTextInfo().direction;
  }

  getWeightUnit() {
    // === 'IMPERIAL' ? 'LBS' : 'KG';
    return this.siteContextService.getSiteInfo().weightUnit as SiteWeightUnit;
  }

  getShortWeightUnit() {
    const weightUnit = this.siteContextService.getSiteInfo().weightUnit as SiteWeightUnit;
    switch (weightUnit) {
      case SiteWeightUnit.KILOGRAM:
        return 'KG';
      case SiteWeightUnit.POUND:
        return 'LB';
      default:
        return weightUnit;
    }
  }

  getVolumeUnit() {
    return this.siteContextService.getSiteInfo().unit === 'IMPERIAL' ? 'Gallon' : 'CBM';
  }

  getDateAndTimeConfig(ignoreSite?) {
    const dateAndTimeFormats = this.splitConstants.dateAndTimeFormats;
    let language;

    if (ignoreSite) {
      language = this.$translate.use()
    } else {
      language = this.getLocaleCode();
    }

    if (!language) {
      language = 'en';
    }
    language = language.substr(0, 2);
    return dateAndTimeFormats[language] || dateAndTimeFormats['en'];
  }

  getBarcodeConfig() {
    const barcode = this.getBarcodeStorage();
    return barcode || {enable: false};
  }

  setBarcodeConfig(isEnabled) {
    const barcode = {
      enabled: isEnabled
    };
    this.setBarcodeStorage(barcode)
  }

  getSiteDeliverySettings(siteId?) {
    if (!siteId) {
      siteId = this.getSiteId();
    }
    const siteDeliveriesSettings = this.storage.getItem('siteDeliveriesSettings');
    const serviceDefaultColumns = this.commonsConstants.viewsTableColumns[ServicesViewsStateType.DELIVERIES_TABLE_STATE];
    const serviceDefaultColumnsMap = this.commonsConstants.viewsTableColumns[ServicesViewsStateType.DELIVERIES_MAP_STATE];
    let deliverySettings;


    if (siteDeliveriesSettings) {
      deliverySettings = JSON.parse(siteDeliveriesSettings);
    }

    if (!deliverySettings || !deliverySettings[siteId]) {
      deliverySettings = {};
      deliverySettings[siteId] = {};
    }

    if (!deliverySettings[siteId].actionType) {
      deliverySettings[siteId].actionType = 'bar';
    }

    // if (!deliverySettings[siteId].defaultGroup) {
    //   deliverySettings[siteId].defaultGroup = 'Unassigned';
    // }

    if (!deliverySettings[siteId].sortTimeWindow && !deliverySettings[siteId].sortAccountName) {
      deliverySettings[siteId].sortTimeWindow = 'desc';
    }

    if (!deliverySettings[siteId].columns ||
      !_.isEqual(_.size(serviceDefaultColumns), _.size(deliverySettings[siteId].columns))) {
      deliverySettings[siteId].columns = serviceDefaultColumns;
    }

    if (!deliverySettings[siteId].mapColumns ||
      !_.isEqual(_.size(serviceDefaultColumnsMap), _.size(deliverySettings[siteId].mapColumns))) {
      deliverySettings[siteId].mapColumns = serviceDefaultColumnsMap;
    }

    if (!this.siteContextService.getSiteInfo().exposeAgentBranchesOnTheService) {
      deliverySettings[siteId].columns.agentOperationalUnits = false;
      deliverySettings[siteId].mapColumns.agentOperationalUnits = false;
    }

    return deliverySettings[siteId];
  }

  setSiteDeliverySettings(siteSettings, siteId?) {
    if (!siteId) {
      siteId = this.getSiteId();
    }
    const siteDeliveriesSettings = this.storage.getItem('siteDeliveriesSettings');
    let deliverySettings;

    if (siteDeliveriesSettings) {
      deliverySettings = JSON.parse(siteDeliveriesSettings);
    }
    if (!deliverySettings) {
      deliverySettings = {};
    }

    deliverySettings[siteId] = siteSettings;
    if (!deliverySettings[siteId].actionType) {
      deliverySettings[siteId].actionType = 'bar';
    }

    this.storage.setItem('siteDeliveriesSettings', JSON.stringify(deliverySettings));
  }

  getSite3plServiceSettings(siteId?) {
    if (!siteId) {
      siteId = this.getSiteId();
    }
    const siteThreePlServicesSettings = this.storage.getItem('siteThreePlServicesSettings');
    const threePlDefaultColumns = this.deliveriesConstants.threePlServicesTableColumns;
    let threePlServicesSettings;

    if (siteThreePlServicesSettings) {
      threePlServicesSettings = JSON.parse(siteThreePlServicesSettings);
    }

    if (!threePlServicesSettings || !threePlServicesSettings[siteId]) {
      threePlServicesSettings = {};
      threePlServicesSettings[siteId] = {};
    }

    // if (!deliverySettings[siteId].actionType) {
    //   deliverySettings[siteId].actionType = 'bar';
    // }

    if (!threePlServicesSettings[siteId].defaultGroup) {
      threePlServicesSettings[siteId].defaultGroup = 'UNASSIGNED';
    }

    if (!threePlServicesSettings[siteId].columns ||
      !_.isEqual(_.size(threePlDefaultColumns), _.size(threePlServicesSettings[siteId].columns))) {
      threePlServicesSettings[siteId].columns = threePlDefaultColumns;
    }

    return threePlServicesSettings[siteId];
  }

  setSite3plServiceSettings(siteSettings, siteId?) {

    if (!siteId) {
      siteId = this.getSiteId();
    }

    const siteThreePlServicesSettings = this.storage.getItem('siteThreePlServicesSettings');
    let threePlServicesSettings;

    if (siteThreePlServicesSettings) {
      threePlServicesSettings = JSON.parse(siteThreePlServicesSettings);
    }

    if (!threePlServicesSettings) {
      threePlServicesSettings = {};
    }

    threePlServicesSettings[siteId] = siteSettings;
    if (!threePlServicesSettings[siteId].actionType) {
      threePlServicesSettings[siteId].actionType = 'bar';
    }
    this.storage.setItem('siteThreePlServicesSettings', JSON.stringify(threePlServicesSettings));
  }

  getServicesMetadataSettings() {
    const servicesSettings = this.getSiteDeliverySettings();
    return servicesSettings.dynamicColumns;
  }


  getAccountMetadataSettings() {
    const settings = this.getSiteAccountSettings();
    return settings.dynamicColumns;
  }

  getThirdPartyServicesMetadataSettings() {
    const thirdPartyServicesSettings = this.getSite3plServiceSettings();
    return thirdPartyServicesSettings.dynamicColumns;
  }

  getTasksMetadataSettings() {
    const servicesSettings = this.getSiteDeliverySettings();
    return servicesSettings.taskDynamicColumns;
  }

  getOmsMetadataSettings() {
    const omsSettings = this.getSiteOmsSettings();
    return omsSettings.dynamicColumns;
  }

  getRoutesMetadataSettings() {
    const servicesSettings = this.getSitePlanningRoutesSettings();
    return servicesSettings.dynamicColumns;
  }

  getSiteOmsSettings(siteId?) {
    if (!siteId) {
      siteId = this.getSiteId();
    }

    const siteOmsSettings = this.storage.getItem('siteOmsSettings');
    const omsDefaultColumnsMap = this.omsConstants.tableColumnsMap;
    let omvViewSettings;

    if (siteOmsSettings) {
      omvViewSettings = JSON.parse(siteOmsSettings);
    }

    if (!omvViewSettings) {
      omvViewSettings = {};
    }

    if (!omvViewSettings[siteId]) {
      omvViewSettings[siteId] = {};
    }

    if (!omvViewSettings[siteId].defaultGroup) {
      omvViewSettings[siteId].defaultGroup = OmsOrdersGroupType.NotScheduled;
    }

    if (_.isUndefined(omvViewSettings[siteId].filterByCreatedAt)) {
      omvViewSettings[siteId].filterByCreatedAt = true;
    }

    let doReset = false;
    _.forOwn(omsDefaultColumnsMap, (value, key) => {
      if (!value.size) {
        doReset = true;
      }
    })

    if (!!doReset || !omvViewSettings[siteId].columns ||
      !_.isEqual(_.size(omsDefaultColumnsMap), _.size(omvViewSettings[siteId].columns))) {
      omvViewSettings[siteId].columns = omsDefaultColumnsMap;
    }

    // if (!omvViewSettings[siteId].dynamicColumns ||
    //   !_.isEqual(_.size(omsDynamicDefaultColumnsMap), _.size(omvViewSettings[siteId].dynamicColumns))) {
    //   omvViewSettings[siteId].dynamicColumns = omsDynamicDefaultColumnsMap;
    // }

    return omvViewSettings[siteId];
  }

  setSiteOmsSettings(siteSettings, siteId?) {
    if (!siteId) {
      siteId = this.getSiteId();
    }
    const siteOmsSettings = this.storage.getItem('siteOmsSettings');
    let omvViewSettings;

    if (siteOmsSettings) {
      omvViewSettings = JSON.parse(siteOmsSettings);
    }
    if (!omvViewSettings) {
      omvViewSettings = {};
    }

    omvViewSettings[siteId] = siteSettings;
    this.storage.setItem('siteOmsSettings', JSON.stringify(omvViewSettings));
  }

  getSitePlanningRoutesSettings(siteId?) {
    if (!siteId) {
      siteId = this.getSiteId();
    }

    const sitePlanningRoutesSettings = this.storage.getItem('sitePlanningRoutesSettings');
    const planningRoutesDefaultColumnsMap = this.leanPlanningConstants.tableColumnsMap;
    let planningRoutesViewSettings;

    if (sitePlanningRoutesSettings) {
      planningRoutesViewSettings = JSON.parse(sitePlanningRoutesSettings);
    }

    if (!planningRoutesViewSettings) {
      planningRoutesViewSettings = {};
    }

    if (!planningRoutesViewSettings[siteId]) {
      planningRoutesViewSettings[siteId] = {};
    }

    if (!planningRoutesViewSettings[siteId].columns ||
      !_.isEqual(_.size(planningRoutesDefaultColumnsMap), _.size(planningRoutesViewSettings[siteId].columns))) {
      planningRoutesViewSettings[siteId].columns = planningRoutesDefaultColumnsMap;
    }

    if (!this.siteContextService.getSiteInfo().exposeAgentBranchesOnTheService) {
      planningRoutesViewSettings[siteId].columns.agentOperationalUnits.hide = true;
    }

    return planningRoutesViewSettings[siteId];
  }

  setSitePlanningRoutesSettings(siteSettings, siteId?) {
    if (!siteId) {
      siteId = this.getSiteId();
    }
    const sitePlanningRoutesSettings = this.storage.getItem('sitePlanningRoutesSettings');
    let planningRoutesViewSettings;

    if (sitePlanningRoutesSettings) {
      planningRoutesViewSettings = JSON.parse(sitePlanningRoutesSettings);
    }
    if (!planningRoutesViewSettings) {
      planningRoutesViewSettings = {};
    }

    planningRoutesViewSettings[siteId] = siteSettings;

    this.storage.setItem('sitePlanningRoutesSettings', JSON.stringify(planningRoutesViewSettings));
  }

  getFulfillmentMetadataSettings() {
    const fulfillmentSettings = this.getSiteFulfillmentSettings();
    return fulfillmentSettings.dynamicColumns;
  }

  getSiteFulfillmentSettings(siteId?) {
    if (!siteId) {
      siteId = this.getSiteId();
    }

    const siteFulfillmentSettings = this.storage.getItem('siteFulfillmentSettings');
    const fulfillmentLinesDefaultColumnsMap = this.FulfillmentConstants.fulfillmentLinesTableColumnsMap;
    const fulfillmentStepsDefaultColumnsMap = this.FulfillmentConstants.fulfillmentStepsTableColumnsMap;
    let fulfillmentViewSettings;

    if (siteFulfillmentSettings) {
      fulfillmentViewSettings = JSON.parse(siteFulfillmentSettings);
    }

    if (!fulfillmentViewSettings) {
      fulfillmentViewSettings = {};
    }

    if (!fulfillmentViewSettings[siteId]) {
      fulfillmentViewSettings[siteId] = {};
    }

    if (!fulfillmentViewSettings[siteId].linesColumns ||
      !_.isEqual(_.size(fulfillmentLinesDefaultColumnsMap), _.size(fulfillmentViewSettings[siteId].linesColumns))) {
      fulfillmentViewSettings[siteId].linesColumns = fulfillmentLinesDefaultColumnsMap;
    }

    if (!fulfillmentViewSettings[siteId].stepsColumns ||
      !_.isEqual(_.size(fulfillmentStepsDefaultColumnsMap), _.size(fulfillmentViewSettings[siteId].stepsColumns))) {
      fulfillmentViewSettings[siteId].stepsColumns = fulfillmentStepsDefaultColumnsMap;
    }

    return fulfillmentViewSettings[siteId];
  }

  setSiteFulfillmentSettings(siteSettings, siteId?) {
    if (!siteId) {
      siteId = this.getSiteId();
    }
    const siteFulfillmentSettings = this.storage.getItem('siteFulfillmentSettings');
    let fulfillmentViewSettings;

    if (siteFulfillmentSettings) {
      fulfillmentViewSettings = JSON.parse(siteFulfillmentSettings);
    }
    if (!fulfillmentViewSettings) {
      fulfillmentViewSettings = {};
    }

    fulfillmentViewSettings[siteId] = siteSettings;
    this.storage.setItem('siteFulfillmentSettings', JSON.stringify(fulfillmentViewSettings));
  }

  getSiteAccountSettings(siteId?) {
    if (!siteId) {
      siteId = this.getSiteId();
    }
    const siteAccountSettings = this.storage.getItem('siteAccountSettings');
    let acccountViewSettings;

    if (siteAccountSettings) {
      acccountViewSettings = JSON.parse(siteAccountSettings);
    }

    if (!acccountViewSettings) {
      acccountViewSettings = {};
    }

    if (!acccountViewSettings[siteId]) {
      acccountViewSettings[siteId] = {};
    }

    return acccountViewSettings[siteId];
  }

  setSiteAccountSettings(siteSettings, siteId?) {
    if (!siteId) {
      siteId = this.getSiteId();
    }
    const siteAccountSettings = this.storage.getItem('siteAccountSettings');
    let accountViewSettings;

    if (siteAccountSettings) {
      accountViewSettings = JSON.parse(siteAccountSettings);
    }
    if (!accountViewSettings) {
      accountViewSettings = {};
    }

    accountViewSettings[siteId] = siteSettings;
    this.storage.setItem('siteAccountSettings', JSON.stringify(accountViewSettings));
  }

  getSiteFulfillmentStepSettings(siteId?) {
    if (!siteId) {
      siteId = this.getSiteId();
    }
    const siteFulfillmentStepSettings = this.storage.getItem('siteFulfillmentStepSettings');
    let fulfillmentStepViewSettings;

    if (siteFulfillmentStepSettings) {
      fulfillmentStepViewSettings = JSON.parse(siteFulfillmentStepSettings);
    }

    if (!fulfillmentStepViewSettings) {
      fulfillmentStepViewSettings = {};
    }

    if (!fulfillmentStepViewSettings[siteId]) {
      fulfillmentStepViewSettings[siteId] = {};
    }

    return fulfillmentStepViewSettings[siteId];
  }

  setSiteFulfillmentStepSettings(siteSettings, siteId?) {
    if (!siteId) {
      siteId = this.getSiteId();
    }
    const siteFulfillmentStepSettings = this.storage.getItem('siteFulfillmentStepSettings');
    let fulfillmentStepViewSettings;

    if (siteFulfillmentStepSettings) {
      fulfillmentStepViewSettings = JSON.parse(siteFulfillmentStepSettings);
    }
    if (!fulfillmentStepViewSettings) {
      fulfillmentStepViewSettings = {};
    }

    fulfillmentStepViewSettings[siteId] = siteSettings;
    this.storage.setItem('siteFulfillmentStepSettings', JSON.stringify(fulfillmentStepViewSettings));
  }

  getSiteFulfillmentStepItemSettings() {

    const siteId = this.getSiteId();

    const siteFulfillmentStepItemSettings = this.storage.getItem('siteFulfillmentStepItemSettings');
    const fulfillmentStepItemDefaultSettings = this.FulfillmentConstants.fulfillmentStepItemSections;

    let fulfillmentStepItemSettings;

    if (siteFulfillmentStepItemSettings) {
      fulfillmentStepItemSettings = JSON.parse(siteFulfillmentStepItemSettings);
    }

    if (!fulfillmentStepItemSettings || !fulfillmentStepItemSettings[siteId]) {
      fulfillmentStepItemSettings = {};
      fulfillmentStepItemSettings[siteId] = {};
    }

    if (!Object.keys(fulfillmentStepItemSettings[siteId]).length) {
      fulfillmentStepItemSettings[siteId] = fulfillmentStepItemDefaultSettings;
    }
    return fulfillmentStepItemSettings[siteId];
  }

  setSiteFulfillmentStepItemSettings(siteSettings: any) {

    const siteId = this.getSiteId();

    const siteFulfillmentStepItemSettings = this.storage.getItem('siteFulfillmentStepItemSettings');
    let fulfillmentStepItemViewSettings;

    if (siteFulfillmentStepItemSettings) {
      fulfillmentStepItemViewSettings = JSON.parse(siteFulfillmentStepItemSettings);
    }
    if (!fulfillmentStepItemViewSettings) {
      fulfillmentStepItemViewSettings = {};
    }

    fulfillmentStepItemViewSettings[siteId] = siteSettings;
    this.storage.setItem('siteFulfillmentStepItemSettings', JSON.stringify(fulfillmentStepItemViewSettings));
  }

  getSiteBillingOrdersSettings(siteId?) {
    if (!siteId) {
      siteId = this.getSiteId();
    }

    const siteSettings = this.storage.getItem('siteBillingOrdersSettings');
    const defaultColumnsMap = this.billingOrdersConstants.tableColumnsMap;
    let ViewSettings;

    if (siteSettings) {
      ViewSettings = JSON.parse(siteSettings);
    }

    if (!ViewSettings) {
      ViewSettings = {};
    }

    if (!ViewSettings[siteId]) {
      ViewSettings[siteId] = {};
    }

    if (_.isUndefined(ViewSettings[siteId].filterByOrderCompletedDate)) {
      ViewSettings[siteId].filterByOrderCompletedDate = true;
    }

    if (!ViewSettings[siteId].columns ||
      !_.isEqual(_.size(defaultColumnsMap), _.size(ViewSettings[siteId].columns))) {
      ViewSettings[siteId].columns = defaultColumnsMap;
    }

    return ViewSettings[siteId];
  }

  setSiteBillingOrdersSettings(siteSettings, siteId?) {
    if (!siteId) {
      siteId = this.getSiteId();
    }
    const currentSiteSettings = this.storage.getItem('siteBillingOrdersSettings');
    let viewSettings;

    if (currentSiteSettings) {
      viewSettings = JSON.parse(currentSiteSettings);
    }
    if (!viewSettings) {
      viewSettings = {};
    }

    viewSettings[siteId] = siteSettings;
    this.storage.setItem('siteBillingOrdersSettings', JSON.stringify(viewSettings));
  }

  getSiteServicesAssignAgentSettings(siteId?) {
    if (!siteId) {
      siteId = this.getSiteId();
    }

    const siteSettings = this.storage.getItem('siteServicesAssignAgentSettings');
    const defaultColumnsMap = this.deliveriesConstants.servicesAssignAgentTableColumns;
    let ViewSettings;

    if (siteSettings) {
      ViewSettings = JSON.parse(siteSettings);
    }

    if (!ViewSettings) {
      ViewSettings = {};
    }

    if (!ViewSettings[siteId]) {
      ViewSettings[siteId] = {};
    }

    if (!ViewSettings[siteId].columns ||
      !_.isEqual(_.size(defaultColumnsMap), _.size(ViewSettings[siteId].columns))) {
      ViewSettings[siteId].columns = defaultColumnsMap;
    }

    return ViewSettings[siteId];
  }

  setSiteServicesAssignAgentSettings(siteSettings, siteId?) {
    if (!siteId) {
      siteId = this.getSiteId();
    }
    const currentSiteSettings = this.storage.getItem('siteServicesAssignAgentSettings');
    let viewSettings;

    if (currentSiteSettings) {
      viewSettings = JSON.parse(currentSiteSettings);
    }
    if (!viewSettings) {
      viewSettings = {};
    }

    viewSettings[siteId] = siteSettings;
    this.storage.setItem('siteServicesAssignAgentSettings', JSON.stringify(viewSettings));
  }

  getSiteServicesAssignRouteSettings(siteId?) {
    if (!siteId) {
      siteId = this.getSiteId();
    }

    const siteSettings = this.storage.getItem('siteServicesAssignRouteSettings');
    const defaultColumnsMap = this.deliveriesConstants.servicesAssignRouteTableColumns;
    let ViewSettings;

    if (siteSettings) {
      ViewSettings = JSON.parse(siteSettings);
    }

    if (!ViewSettings) {
      ViewSettings = {};
    }

    if (!ViewSettings[siteId]) {
      ViewSettings[siteId] = {};
    }

    if (!ViewSettings[siteId].columns ||
      !_.isEqual(_.size(defaultColumnsMap), _.size(ViewSettings[siteId].columns))) {
      ViewSettings[siteId].columns = defaultColumnsMap;
    }

    return ViewSettings[siteId];
  }

  setSiteServicesAssignRouteSettings(siteSettings, siteId?) {
    if (!siteId) {
      siteId = this.getSiteId();
    }
    const currentSiteSettings = this.storage.getItem('siteServicesAssignRouteSettings');
    let viewSettings;

    if (currentSiteSettings) {
      viewSettings = JSON.parse(currentSiteSettings);
    }
    if (!viewSettings) {
      viewSettings = {};
    }

    viewSettings[siteId] = siteSettings;
    this.storage.setItem('siteServicesAssignRouteSettings', JSON.stringify(viewSettings));
  }

  getSiteServicesAssignResourceAllocationSettings(siteId?) {
    if (!siteId) {
      siteId = this.getSiteId();
    }
    const siteSettings = this.storage.getItem('siteServicesAssignResourceAllocationSettings');
    const defaultColumnsMap: ServicesAssignResourceColumnsModel = this.deliveriesConstants.servicesAssignResourceAllocationTableColumns;
    let ViewSettings;
    if (siteSettings) {
      ViewSettings = JSON.parse(siteSettings);
    }
    if (!ViewSettings) {
      ViewSettings = {};
    }
    if (!ViewSettings[siteId]) {
      ViewSettings[siteId] = {};
    }
    if (!ViewSettings[siteId].columns ||
      !_.isEqual(_.size(defaultColumnsMap.primary), _.size(ViewSettings[siteId].columns?.primary)) ||
      !_.isEqual(_.size(defaultColumnsMap.load), _.size(ViewSettings[siteId].columns?.load))) {
      ViewSettings[siteId].columns = defaultColumnsMap;
    }
    return ViewSettings[siteId];
  }

  setSiteServicesAssignResourceAllocationSettings(siteSettings, siteId?) {
    if (!siteId) {
      siteId = this.getSiteId();
    }
    const currentSiteSettings = this.storage.getItem('siteServicesAssignResourceAllocationSettings');
    let viewSettings;
    if (currentSiteSettings) {
      viewSettings = JSON.parse(currentSiteSettings);
    }
    if (!viewSettings) {
      viewSettings = {};
    }
    viewSettings[siteId] = siteSettings;
    this.storage.setItem('siteServicesAssignResourceAllocationSettings', JSON.stringify(viewSettings));
  }

  getSitePackageDetailsSettings(siteId?) {
    if (!siteId) {
      siteId = this.getSiteId();
    }
    const siteSettings = this.storage.getItem('sitePackageDetailsSettings');
    const defaultColumnsMap = this.commonsConstants.packageDetailsTableColumns;
    let ViewSettings;
    if (siteSettings) {
      ViewSettings = JSON.parse(siteSettings);
    }
    if (!ViewSettings) {
      ViewSettings = {};
    }
    if (!ViewSettings[siteId]) {
      ViewSettings[siteId] = {};
    }
    if (!ViewSettings[siteId].columns ||
      !_.isEqual(_.size(defaultColumnsMap), _.size(ViewSettings[siteId].columns))) {
      ViewSettings[siteId].columns = defaultColumnsMap;
    }
    return ViewSettings[siteId];
  }

  setSitePackageDetailsSettings(siteSettings, siteId?) {
    if (!siteId) {
      siteId = this.getSiteId();
    }
    const currentSiteSettings = this.storage.getItem('sitePackageDetailsSettings');
    let viewSettings;
    if (currentSiteSettings) {
      viewSettings = JSON.parse(currentSiteSettings);
    }
    if (!viewSettings) {
      viewSettings = {};
    }
    viewSettings[siteId] = siteSettings;
    this.storage.setItem('sitePackageDetailsSettings', JSON.stringify(viewSettings));
  }

  getSiteMetaData(siteId?) {
    let siteMetaData = JSON.parse(this.storage.getItem('siteMetaData'));
    if (!siteId) {
      siteId = this.getSiteId();
    }
    if (!siteMetaData) {
      siteMetaData = {};
    }
    return siteMetaData[siteId];
  }

  setSiteMetaData(metaData, siteId?) {
    let siteMetaData = JSON.parse(this.storage.getItem('siteMetaData'));
    if (!siteId) {
      siteId = this.getSiteId();
    }
    if (!siteMetaData) {
      siteMetaData = {};
    }
    siteMetaData[siteId] = metaData;
    this.storage.setItem('siteMetaData', JSON.stringify(siteMetaData));
  }

  getSiteAgentsSettings(siteId?) {
    if (!siteId) {
      siteId = this.getSiteId();
    }

    const siteAgentsSettings = this.storage.getItem('siteAgentsSettings');
    const agentsDefaultColumnsMap = this.agentConstants.agentsTableColumnsMap;
    let agentsViewSettings;

    if (siteAgentsSettings) {
      agentsViewSettings = JSON.parse(siteAgentsSettings);
    }

    if (!agentsViewSettings) {
      agentsViewSettings = {};
    }

    if (!agentsViewSettings[siteId]) {
      agentsViewSettings[siteId] = {};
    }

    if (!agentsViewSettings[siteId].columns ||
      !_.isEqual(_.size(agentsDefaultColumnsMap), _.size(agentsViewSettings[siteId].columns))) {
      agentsViewSettings[siteId].columns = agentsDefaultColumnsMap;
    }

    return agentsViewSettings[siteId];
  }

  setSiteAgentsSettings(siteSettings?, siteId?) {
    if (!siteId) {
      siteId = this.getSiteId();
    }
    const siteAgentsSettings = this.storage.getItem('siteAgentsSettings');
    let agentsViewSettings;

    if (siteAgentsSettings) {
      agentsViewSettings = JSON.parse(siteAgentsSettings);
    }
    if (!agentsViewSettings) {
      agentsViewSettings = {};
    }

    agentsViewSettings[siteId] = siteSettings;
    this.storage.setItem('siteAgentsSettings', JSON.stringify(agentsViewSettings));
  }

  setSiteAccountsSettings(siteSettings?, siteId?) {
    if (!siteId) {
      siteId = this.getSiteId();
    }
    const siteAccountsSettings = this.storage.getItem('siteAccountsSettings');
    let accountsViewSettings;

    if (siteAccountsSettings) {
      accountsViewSettings = JSON.parse(siteAccountsSettings);
    }
    if (!accountsViewSettings) {
      accountsViewSettings = {};
    }

    accountsViewSettings[siteId] = siteSettings;
    this.storage.setItem('siteAccountsSettings', JSON.stringify(accountsViewSettings));
  }

  getSiteExpensesSettings(siteId?) {
    if (!siteId) {
      siteId = this.getSiteId();
    }

    const siteExpensesSettings = this.storage.getItem('siteExpensesSettings');
    const expensesDefaultColumnsMap = this.expensesConstants.expensesTableColumnsMap;
    let expensesViewSettings;

    if (siteExpensesSettings) {
      expensesViewSettings = JSON.parse(siteExpensesSettings);
    }

    if (!expensesViewSettings) {
      expensesViewSettings = {};
    }

    if (!expensesViewSettings[siteId]) {
      expensesViewSettings[siteId] = {};
    }

    if (!expensesViewSettings[siteId].columns ||
      !_.isEqual(_.size(expensesDefaultColumnsMap), _.size(expensesViewSettings[siteId].columns))) {
      expensesViewSettings[siteId].columns = expensesDefaultColumnsMap;
    }

    return expensesViewSettings[siteId];
  }

  setSiteExpensesSettings(siteSettings, siteId) {
    if (!siteId) {
      siteId = this.getSiteId();
    }
    const siteExpensesSettings = this.storage.getItem('siteExpensesSettings');
    let expensesViewSettings;

    if (siteExpensesSettings) {
      expensesViewSettings = JSON.parse(siteExpensesSettings);
    }
    if (!expensesViewSettings) {
      expensesViewSettings = {};
    }

    expensesViewSettings[siteId] = siteSettings;
    this.storage.setItem('siteExpensesSettings', JSON.stringify(expensesViewSettings));
  }

  getSiteTimekeepingSettings(siteId?) {
    if (!siteId) {
      siteId = this.getSiteId();
    }

    const siteTimekeepingSettings = this.storage.getItem('siteTimekeepingSettings');
    const timekeepingDefaultColumnsMap = this.timekeepingConstants.timekeepingTableColumnsMap;
    let timekeepingViewSettings;

    if (siteTimekeepingSettings) {
      timekeepingViewSettings = JSON.parse(siteTimekeepingSettings);
    }

    if (!timekeepingViewSettings) {
      timekeepingViewSettings = {};
    }

    if (!timekeepingViewSettings[siteId]) {
      timekeepingViewSettings[siteId] = {};
    }

    if (!timekeepingViewSettings[siteId].columns ||
      !_.isEqual(_.size(timekeepingDefaultColumnsMap), _.size(timekeepingViewSettings[siteId].columns))) {
      timekeepingViewSettings[siteId].columns = timekeepingDefaultColumnsMap;
    }

    return timekeepingViewSettings[siteId];
  }

  setSiteTimekeepingSettings(siteSettings, siteId?) {
    if (!siteId) {
      siteId = this.getSiteId();
    }
    const siteTimekeepingSettings = this.storage.getItem('siteTimekeepingSettings');
    let timekeepingViewSettings;

    if (siteTimekeepingSettings) {
      timekeepingViewSettings = JSON.parse(siteTimekeepingSettings);
    }
    if (!timekeepingViewSettings) {
      timekeepingViewSettings = {};
    }

    timekeepingViewSettings[siteId] = siteSettings;
    this.storage.setItem('siteTimekeepingSettings', JSON.stringify(timekeepingViewSettings));
  }

  getSiteItinerarySettings(siteId) {
    const siteItinerarySettings = this.storage.getItem('siteItinerarySettings');
    let itinerarySettings;

    if (siteItinerarySettings) {
      itinerarySettings = JSON.parse(siteItinerarySettings);
    }

    if (!itinerarySettings || !itinerarySettings[siteId]) {
      itinerarySettings = {};
      itinerarySettings[siteId] = {};
    }

    if (!itinerarySettings[siteId].agentsGroup) {
      itinerarySettings[siteId].agentsGroup = 'Active';
    }
    return itinerarySettings[siteId];
  }

  setSiteItinerarySettings(settings, siteId?: string) {
    if (!siteId) {
      siteId = this.getSiteId();
    }

    const siteItinerarySettings = this.storage.getItem('siteItinerarySettings');
    let itinerarySettings;

    if (siteItinerarySettings) {
      itinerarySettings = JSON.parse(siteItinerarySettings);
    }
    if (!itinerarySettings) {
      itinerarySettings = {};
    }

    itinerarySettings[siteId] = settings;
    this.storage.setItem('siteItinerarySettings', JSON.stringify(itinerarySettings));
  }

  getSiteItinerariesTasksSettings(siteId: string): ItinerariesTasksSettings {

    if (!siteId) {
      siteId = this.getSiteId();
    }
    const siteItinerariesTasksSettings = this.storage.getItem('siteItinerariesTasksSettings');
    const itinerariesTasksDefaultSettings = this.agentConstants.itinerariesTasksTableSections as ItinerariesTasksSettings;

    let itinerariesTasksSettings;

    if (siteItinerariesTasksSettings) {
      itinerariesTasksSettings = JSON.parse(siteItinerariesTasksSettings);
    }

    if (!itinerariesTasksSettings || !itinerariesTasksSettings[siteId]) {
      itinerariesTasksSettings = {};
      itinerariesTasksSettings[siteId] = {};
    }

    if (!(itinerariesTasksSettings[siteId].sections)) {
      itinerariesTasksSettings[siteId].sections = itinerariesTasksDefaultSettings.sections;
    }
    return itinerariesTasksSettings[siteId] as ItinerariesTasksSettings;
  }

  setSiteItinerariesTasksSettings(siteSettings: ItinerariesTasksSettings, siteId?: string) {
    if (!siteId) {
      siteId = this.getSiteId();
    }
    const siteItinerariesTasksSettings = this.storage.getItem('siteItinerariesTasksSettings');
    let itinerariesTasksViewSettings;

    if (siteItinerariesTasksSettings) {
      itinerariesTasksViewSettings = JSON.parse(siteItinerariesTasksSettings);
    }
    if (!itinerariesTasksViewSettings) {
      itinerariesTasksViewSettings = {};
    }

    itinerariesTasksViewSettings[siteId] = siteSettings;
    this.storage.setItem('siteItinerariesTasksSettings', JSON.stringify(itinerariesTasksViewSettings));
  }

  getSitePlanningRouteOverviewSettings(siteId: string): PlanningRouteOverviewSettingsModel {

    if (!siteId) {
      siteId = this.getSiteId();
    }
    const sitePlanningRouteOverviewSettings = this.storage.getItem('sitePlanningRouteOverviewSettings');
    const planningRouteOverviewDefaultSettings = this.planningConstants.projectRouteOverviewTableSections as PlanningRouteOverviewSettingsModel;

    let planningRouteOverviewSettings;

    if (sitePlanningRouteOverviewSettings) {
      planningRouteOverviewSettings = JSON.parse(sitePlanningRouteOverviewSettings);
    }

    if (!planningRouteOverviewSettings || !planningRouteOverviewSettings[siteId]) {
      planningRouteOverviewSettings = {};
      planningRouteOverviewSettings[siteId] = {};
    }

    if (!(planningRouteOverviewSettings[siteId].sections)) {
      planningRouteOverviewSettings[siteId].sections = planningRouteOverviewDefaultSettings.sections;
    }
    return planningRouteOverviewSettings[siteId] as ItinerariesTasksSettings;
  }

  setSitePlanningRouteOverviewSettings(siteSettings: PlanningRouteOverviewSettingsModel, siteId?: string) {
    if (!siteId) {
      siteId = this.getSiteId();
    }
    const sitePlanningRouteOverviewSettings = this.storage.getItem('sitePlanningRouteOverviewSettings');
    let planningRouteOverviewSettings;

    if (sitePlanningRouteOverviewSettings) {
      planningRouteOverviewSettings = JSON.parse(sitePlanningRouteOverviewSettings);
    }
    if (!planningRouteOverviewSettings) {
      planningRouteOverviewSettings = {};
    }

    planningRouteOverviewSettings[siteId] = siteSettings;
    this.storage.setItem('sitePlanningRouteOverviewSettings', JSON.stringify(planningRouteOverviewSettings));
  }

  getRoutesLoadingSettings(siteId) {
    if (!siteId) {
      siteId = this.getSiteId();
    }
    const siteRoutesLoadingSettings = this.storage.getItem('siteRoutesLoadingSettings');
    let settings;

    if (siteRoutesLoadingSettings) {
      settings = JSON.parse(siteRoutesLoadingSettings);
    }

    if (!settings || !settings[siteId]) {
      settings = {};
      settings[siteId] = {};
    }
    if (!settings[siteId].routesAutomation) {
      settings[siteId].routesAutomation = 'MANUAL'; // MANUAL/AUTO
    }

    // if (!settings[siteId].routesAutomationTime) {
    //   settings[siteId].routesAutomationTime = 1; // MANUAL/AUTO
    // }

    if (!settings[siteId].routeColumnAutomation) {
      settings[siteId].routeColumnAutomation = 'MANUAL'; // MANUAL/AUTO
    }

    if (!settings[siteId].routeColumnAutomationTime) {
      settings[siteId].routeColumnAutomationTime = 1; // MANUAL/AUTO
    }

    if (!settings[siteId].idType) {
      settings[siteId].idType = 'id'; // id/externalId/displayId
    }

    if (!settings[siteId].orderType) {
      settings[siteId].orderType = 'ORDER'; // ORDER/NAME
    }

    if (_.isUndefined(settings[siteId].showNotPacked)) {
      settings[siteId].showNotPacked = true; // ORDER/NAME
    }

    return settings[siteId];
  }

  setRoutesLoadingSettings(siteSettings, siteId) {
    if (!siteId) {
      siteId = this.getSiteId();
    }
    const siteRoutesLoadingSettings = this.storage.getItem('siteRoutesLoadingSettings');
    let settings;

    if (siteRoutesLoadingSettings) {
      settings = JSON.parse(siteRoutesLoadingSettings);
    }
    if (!settings) {
      settings = {};
    }

    settings[siteId] = siteSettings;

    if (!settings[siteId].routesAutomation) {
      settings[siteId].routesAutomation = 'MANUAL'; // MANUAL/AUTO
    }

    if (!settings[siteId].routeColumnAutomation) {
      settings[siteId].routeColumnAutomation = 'MANUAL'; // MANUAL/AUTO
    }

    if (!settings[siteId].idType) {
      settings[siteId].idType = 'id'; // id/externalId/displayId
    }

    if (!settings[siteId].orderType) {
      settings[siteId].orderType = 'ORDER'; // ORDER/NAME
    }

    if (_.isUndefined(settings[siteId].showNotPacked)) {
      settings[siteId].showNotPacked = true; // ORDER/NAME
    }

    this.storage.setItem('siteRoutesLoadingSettings', JSON.stringify(settings));
  }

  getPrinterProvider() {
    const siteInfo = this.siteContextService.getSiteInfo();
    return siteInfo.printerProviderType;
  }

  getServiceMandatoryOnExpense(): boolean {
    const siteInfo = this.siteContextService.getSiteInfo();
    return siteInfo.serviceMandatoryOnExpense;
  }

  isPrintingBillOfLadingAllowed() {
    return this.siteContextService.getSiteInfo().allowPrintingBillOfLading;
  }

  getOmsPackageTotalWeightIndication() {
    const siteInfo = this.siteContextService.getSiteInfo();
    return siteInfo.isOmsPackageTotalWeight;
  }

  getPackageVolumeIsTotal() {
    const siteInfo = this.siteContextService.getSiteInfo();
    return siteInfo.packageVolumeIsTotal;
  }

  getPackageWeightIsTotal() {
    const siteInfo = this.siteContextService.getSiteInfo();
    return siteInfo.packageWeightIsTotal;
  }

  getDefaultDispatchType() {
    const siteInfo = this.siteContextService.getSiteInfo();
    return _.get(siteInfo, 'omsOrderCustomizedFields.dispatchType');
  }

  isReferenceIdsMandatory(): boolean {
    const siteInfo = this.siteContextService.getSiteInfo();
    return _.get(siteInfo, 'omsOrderCustomizedFields.referenceIdsMandatory');
  }

  getBlockingSearchDatesRangeLimit(): number {
    const siteInfo = this.siteContextService.getSiteInfo();
    return siteInfo.blockingSearchDatesRangeLimit;
  }

  /**
   * The site's configured Search Result Method. Undefined means "send nothing"
   * and let the server fall back to its own default (SMART_SEARCH).
   */
  getSuperSearchSortMethod(): SuperSearchSortMethodType {
    if (!this.siteContextService.isSite()) {
      return undefined;
    }
    return this.siteContextService.getSiteInfo().superSearchSortMethod;
  }

  getSupportedThreePlProviders(): any {
    const siteInfo = this.siteContextService.getSiteInfo();
    return siteInfo.supportedThreePlProviders;
  }

  isOmsPackageTotalValue(): boolean {
    const siteInfo = this.siteContextService.getSiteInfo();
    return siteInfo.isOmsPackageTotalValue;
  }

  isOfflineEnabled(): boolean {
    const siteInfo = this.siteContextService.getSiteInfo();
    return siteInfo.offlineEnabled;
  }

  maxUserIdleTimeMinutes(): number {
    const siteInfo = this.siteContextService.getSiteInfo();
    return siteInfo.maxUserIdleTimeMinutes;
  }

  getIsServiceExternalIdIsMandatory(): boolean {
    const siteInfo = this.siteContextService.getSiteInfo();
    return !!siteInfo.serviceCustomizedFields?.serviceExternalIdIsMandatory;
  }

  getIsOrderExternalIdIsMandatory(): boolean {
    const siteInfo = this.siteContextService.getSiteInfo();
    return !!siteInfo.omsOrderCustomizedFields?.orderExternalIdIsMandatory;
  }

  isAllowOnlyOnePackagePerLineInOms(): boolean {
    const siteInfo = this.siteContextService.getSiteInfo();
    return siteInfo.enableLineSplitPerPackageType;
  }

  getIsOmsPackageDimensionMandatory(): boolean {
    const siteInfo = this.siteContextService.getSiteInfo();
    return !!siteInfo.omsOrderCustomizedFields?.omsPackageDimensionMandatory;
  }

  getServiceBlockResetAction(): boolean {
    const siteInfo = this.siteContextService.getSiteInfo();
    return !!siteInfo.serviceCustomizedFields?.serviceBlockResetAction;
  }

  getAccountBillingCycle(): boolean {
    const siteInfo = this.siteContextService.getSiteInfo();
    return siteInfo.accountBillingCycle;
  }

  getCarrierBillingCycle(): boolean {
    const siteInfo = this.siteContextService.getSiteInfo();
    return siteInfo.carrierBillingCycle;
  }

  getShowMapInServiceView(): boolean {
    return this.siteContextService.getSiteInfo().showMap;
  }

  getSiteDefaultPackageConfiguration(): PackageConfigurationEntity {
    return angular.copy(this.siteContextService.getSiteInfo().defaultPackageConfiguration)
  }

  getSiteDefaultPackageConfigurationFields(): PackageFieldConfigurationModel[] {
    return angular.copy(this.siteContextService.getSiteInfo().defaultPackageConfiguration?.fields)
  }

  getSitePackageConfigurations(): PackageConfigurationEntity[] {
    return angular.copy(this.siteContextService.getSiteInfo().packageConfigurations)
  }

  getSiteIntegrationExportRouteEnabled(): boolean[] {
    return angular.copy(this.siteContextService.getSiteInfo().integrationExportRouteEnabled)
  }

  getCustomPackageFields(): CustomFieldModel[] {
    return angular.copy(this.siteContextService.getSiteInfo().customPackageFields)
  }

  getOmsEditableStatusConfiguration() {
    const siteInfo = this.siteContextService.getSiteInfo();
    return siteInfo.omsOrderEditableStatusPreferences;
  }

  getSupportResourcePriority() {
    const siteInfo = this.siteContextService.getSiteInfo();
    return siteInfo.supportResourcePriority;
  }

  getLimitingSingleTimezone() {
    const siteInfo = this.siteContextService.getSiteInfo();
    return siteInfo.limitingSingleTimezone;
  }

  getTagsToHide() {
    const siteInfo = this.siteContextService.getSiteInfo();
    return siteInfo.tagsToHide;
  }

  getTagsWithColor() {
    return this.siteContextService.getSiteInfo().tagsWithColor;
  }

  getIsNewOrderNotificationsEnabled() {
    const value = this.storage.getItem('isShowNewOrderNotificationsEnabled');
    if (value) {
      return JSON.parse(value);
    } else {
      return true;
    }
  }

  setIsNewOrderNotificationsEnabled(value: boolean) {
    this.storage.setItem('isShowNewOrderNotificationsEnabled', JSON.stringify(value));
  }

  setSelectedTimeZone(zoneId) {
    if (!!zoneId) {
      this.storage.setItem('TIME_ZONE_KEY', zoneId);
    } else {
      this.storage.removeItem('TIME_ZONE_KEY');
    }
    this.moment.tz.setDefault(zoneId);
  }

  getSelectedTimeZone() {
    return this.storage.getItem('TIME_ZONE_KEY');
  }

  getReportUserLastActive() {
    if (!this.siteContextService.isSite()) {
      return false;
    }
    return this.siteContextService.getSiteInfo().reportUserLastActive;
  }

  getExposeAgentBranchesOnTheService() {
    return this.siteContextService.getSiteInfo().exposeAgentBranchesOnTheService;
  }

  getDisableEntityLockStateValue() {
    return this.siteContextService.getSiteInfo().disableEntityLockState;
  }

  getEnableLocationTrackingFilterByDefault() {
    return this.siteContextService.getSiteInfo().enableLocationTrackingFilterByDefault;
  }

  getEnableResourceAgentTypeEditing() {
    return this.siteContextService.getSiteInfo().allowResourceAgentTypeEditing;
  }

  getOmsOrderEnableDisplayAllCustomers(): boolean {
    const siteInfo = this.siteContextService.getSiteInfo();
    return !!siteInfo.omsOrderCustomizedFields?.enableDisplayAllCustomers;
  }

  getServiceEnableDisplayAllCustomers(): boolean {
    const siteInfo = this.siteContextService.getSiteInfo();
    return !!siteInfo.serviceCustomizedFields?.enableDisplayAllCustomers;
  }

  getEnableAllocationBranchSupport(): boolean {
    return this.siteContextService.getSiteInfo().enableAllocationBranchSupport;
  }

  getEnforceSequentialFulfillmentDispatch(): boolean {
    return this.siteContextService.getSiteInfo().enforceSequentialFulfillmentDispatch;
  }

  getValidateRouteAssignment(): boolean {
    return this.siteContextService.getSiteInfo().validateRouteAssignment;
  }

  getEnableOperationalContact(): boolean {
    const siteInfo = this.siteContextService.getSiteInfo();
    return !!siteInfo.serviceCustomizedFields?.enableOperationalContact || !!siteInfo.serviceCustomizedFields?.fulfillmentLegsContacts;
  }

  getCustomFields(): string[] {
    const customizedServicesSettings = this.getServicesMetadataSettings();
    if (customizedServicesSettings) {
      return Object.values(customizedServicesSettings).map(f => f['key']);
    }
    return [];
  }

  getOperationalContactTypes(): string[] {
    const siteInfo = this.siteContextService.getSiteInfo();
    return siteInfo.serviceCustomizedFields?.operationalContactTypes;
  }



  getUpdateExistingRouteResourceDefault(): boolean {
    const siteInfo = this.siteContextService.getSiteInfo();
    return siteInfo.updateExistingRouteResourceDefault;
  }

  getUpdateRouteServicesTimeWindowsDefault(): boolean {
    const siteInfo = this.siteContextService.getSiteInfo();
    return siteInfo.updateRouteServicesTimeWindowsDefault;
  }



  getUseLegacyServiceTime(): boolean {
    return _.isUndefined(this.siteContextService.getSiteInfo().useLegacyServiceTime) ? true : this.siteContextService.getSiteInfo().useLegacyServiceTime;
  }

  getUseLegacyVehicleType(): boolean {
    return _.isUndefined(this.siteContextService.getSiteInfo().useLegacyVehicleType) ? true : this.siteContextService.getSiteInfo().useLegacyVehicleType;
  }

  getEnableAgentVehicleBinding(): boolean {
    const siteInfo = this.siteContextService.getSiteInfo();
    return siteInfo.enableAgentVehicleBinding;  }

  getProgramCodesMap() {
    const programCodeMap = {};

    this.siteContextService.getSiteInfo().programSettings.forEach(program => {
      if (program.code) {
        programCodeMap[program.id] = program.code;
      }
    });

    return programCodeMap;
  }

  getLabelPrinterTemplateType(): string {
    const siteInfo = this.siteContextService.getSiteInfo();
    return siteInfo?.printLabelTemplateType || 'Default'; // ['Default', 'Netcost', 'Bergstrom'];
  }

  getVisitPlanProviders(): string {
    const siteInfo = this.siteContextService.getSiteInfo();
    return siteInfo.visitPlanProviders;
  }

  getIsEnablePlanningJobSplit() {
    const siteInfo = this.siteContextService.getSiteInfo();
    return siteInfo.enablePlanningJobSplit;
  }

  getSupportLayeredPlanning() {
    const siteInfo = this.siteContextService.getSiteInfo();
    return siteInfo.supportLayeredPlanning;
  }

  getBreakTypes() {
    const siteInfo = this.siteContextService.getSiteInfo();
    return siteInfo.breakTypes;
  }

  isTimekeepingEnabled(): boolean {
    const siteInfo = this.siteContextService.getSiteInfo();
    const timekeepingModule = siteInfo?.sitePlan?.modules?.find(module => module.id === 'timekeeping');
    return !!timekeepingModule?.enabled;
  }

  isFieldServiceEnabled(): boolean {
    const siteInfo = this.siteContextService.getSiteInfo();
    const module = siteInfo?.sitePlan?.modules?.find(module => module.id === 'operationalservice');
    return !!module?.enabled;
  }

  // ******************************
  // Internal methods
  // ******************************

  private setBarcodeStorage(barcode) {
    this.storage.setItem('barcode', JSON.stringify(barcode));
  }

  private getBarcodeStorage() {
    const barcode = this.storage.getItem('barcode');
    return JSON.parse(barcode);
  }


}

angular
  .module('bringoz.admin.commons')
  .service('splitService', SplitService);
